"use client";

import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, BookOpen, Keyboard, Network, HelpCircle, ArrowRight, RotateCcw, FileText, CheckSquare, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";

interface HelpDrawerProps {
  open: boolean;
  onClose: () => void;
}

type TabType = "markdown" | "wikilinks" | "shortcuts" | "onboarding";

const SHORTCUTS = [
  { keys: ["⌘/Ctrl", "K"], label: "Buscar / Comandos", desc: "Abre la paleta de comandos y búsqueda" },
  { keys: ["?"], label: "Centro de Ayuda", desc: "Abre / cierra este panel" },
  { keys: ["Esc"], label: "Cerrar", desc: "Cierra diálogos, menús o paneles" },
  { keys: ["Alt", "N"], label: "Nueva nota", desc: "Crea un documento nuevo al instante" },
  { keys: ["Alt", "D"], label: "Dividir pantalla", desc: "Alterna el modo de pantalla dividida" },
  { keys: ["Alt", "G"], label: "Grafo", desc: "Abre el Grafo de Conocimiento en el panel" },
  { keys: ["Alt", "T"], label: "Tema", desc: "Alterna entre modo claro y oscuro" },
];

const MARKDOWN_RULES = [
  { syntax: "# Título 1", label: "Título Principal" },
  { syntax: "## Título 2", label: "Subtítulo" },
  { syntax: "**texto**", label: "Texto en Negrita" },
  { syntax: "*texto*", label: "Texto en Cursiva" },
  { syntax: "- Elemento", label: "Lista con Viñetas" },
  { syntax: "1. Elemento", label: "Lista Numerada" },
  { syntax: "[Enlace](url)", label: "Vínculo Web Externo" },
  { syntax: "> Cita", label: "Cita en bloque" },
  { syntax: "`código`", label: "Código en línea" },
  { syntax: "[ ] Tarea", label: "Casilla de tarea" },
];

export function HelpDrawer({ open, onClose }: HelpDrawerProps) {
  const [activeTab, setActiveTab] = useState<TabType>("markdown");

  // Close Help Drawer on Escape keypress (toggle on "?" is handled in WorkspaceLayout)
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }
    };

    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open, onClose]);

  const startTourNow = () => {
    onClose();
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent("wiki:start-tour"));
    }, 200);
  };

  const restartOnboarding = () => {
    localStorage.removeItem("wiki_arsenal_tour_done");
    localStorage.removeItem("wiki_arsenal_welcome_seen");
    localStorage.removeItem("wiki_arsenal_onboarding_done");
    onClose();
    window.location.reload();
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop blur overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/30 backdrop-blur-xs z-[100]"
          />

          {/* Sliding Panel */}
          <motion.div
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 25, stiffness: 220 }}
            className="fixed right-0 top-0 h-full w-full sm:w-[420px] bg-card/95 border-l border-border/40 shadow-2xl z-[110] flex flex-col backdrop-blur-md"
          >
            {/* Header */}
            <div className="p-5 border-b border-border/20 flex items-center justify-between shrink-0 bg-secondary/10">
              <div className="flex items-center gap-2">
                <HelpCircle className="w-5 h-5 text-primary" />
                <div>
                  <h2 className="text-sm font-bold text-foreground">Centro de Ayuda</h2>
                  <p className="text-[10px] text-muted-foreground">Documentación y accesos rápidos</p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-1 rounded-md text-muted-foreground hover:bg-secondary/60 hover:text-foreground transition-all"
                aria-label="Cerrar panel de ayuda"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Tab navigation */}
            <div className="flex border-b border-border/10 shrink-0 bg-secondary/5 p-1 gap-1">
              <button
                onClick={() => setActiveTab("markdown")}
                className={cn(
                  "flex-1 py-2 text-[11px] font-bold rounded-lg transition-all flex flex-col items-center gap-1",
                  activeTab === "markdown" ? "bg-background text-primary shadow-xs" : "text-muted-foreground hover:text-foreground"
                )}
              >
                <FileText className="w-3.5 h-3.5" />
                Markdown
              </button>
              <button
                onClick={() => setActiveTab("wikilinks")}
                className={cn(
                  "flex-1 py-2 text-[11px] font-bold rounded-lg transition-all flex flex-col items-center gap-1",
                  activeTab === "wikilinks" ? "bg-background text-primary shadow-xs" : "text-muted-foreground hover:text-foreground"
                )}
              >
                <Network className="w-3.5 h-3.5" />
                Wiki-Links
              </button>
              <button
                onClick={() => setActiveTab("shortcuts")}
                className={cn(
                  "flex-1 py-2 text-[11px] font-bold rounded-lg transition-all flex flex-col items-center gap-1",
                  activeTab === "shortcuts" ? "bg-background text-primary shadow-xs" : "text-muted-foreground hover:text-foreground"
                )}
              >
                <Keyboard className="w-3.5 h-3.5" />
                Atajos
              </button>
              <button
                onClick={() => setActiveTab("onboarding")}
                className={cn(
                  "flex-1 py-2 text-[11px] font-bold rounded-lg transition-all flex flex-col items-center gap-1",
                  activeTab === "onboarding" ? "bg-background text-primary shadow-xs" : "text-muted-foreground hover:text-foreground"
                )}
              >
                <Sparkles className="w-3.5 h-3.5" />
                Tours
              </button>
            </div>

            {/* Content area */}
            <div className="flex-1 overflow-y-auto p-5 custom-scrollbar space-y-4">
              {activeTab === "markdown" && (
                <div className="space-y-4">
                  <div>
                    <h3 className="text-xs font-bold text-foreground mb-1">Guía rápida de Markdown</h3>
                    <p className="text-[11px] text-muted-foreground leading-relaxed">
                      El editor admite Markdown estándar para dar formato a tus notas de manera ágil. Aquí tienes los formatos más comunes:
                    </p>
                  </div>

                  <div className="border border-border/40 rounded-xl overflow-hidden divide-y divide-border/10 bg-secondary/15">
                    {MARKDOWN_RULES.map((rule, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2.5 text-xs">
                        <code className="font-mono text-primary font-semibold text-[11px] bg-secondary/40 px-1.5 py-0.5 rounded">
                          {rule.syntax}
                        </code>
                        <span className="text-muted-foreground text-[11px] font-medium">{rule.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {activeTab === "wikilinks" && (
                <div className="space-y-4">
                  <div>
                    <h3 className="text-xs font-bold text-foreground mb-1">¿Qué son los Wiki-Links?</h3>
                    <p className="text-[11px] text-muted-foreground leading-relaxed">
                      Los Wiki-Links te permiten conectar notas instantáneamente. Esto crea una base de conocimiento interconectada en lugar de una estructura jerárquica rígida.
                    </p>
                  </div>

                  <div className="p-4 bg-primary/5 border border-primary/10 rounded-xl space-y-3">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-primary block">Cómo utilizarlos</span>
                    <div className="text-xs text-foreground/90 font-medium leading-relaxed">
                      Escribe <code className="bg-primary/10 text-primary px-1 py-0.5 rounded font-mono">[[Nombre de Nota]]</code> en cualquier parte del editor.
                    </div>
                    <p className="text-[11px] text-muted-foreground leading-relaxed">
                      Al hacerlo, se creará un enlace interactivo. Si la nota ya existe, al hacer clic te redirigirá a ella. Si no existe, Wiki Arsenal la creará automáticamente para ti.
                    </p>
                  </div>

                  <div className="p-3 bg-secondary/30 border border-border/20 rounded-xl">
                    <div className="flex gap-2.5">
                      <Network className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                      <div className="space-y-1">
                        <span className="text-xs font-bold text-foreground block">Grafo de Conocimiento</span>
                        <p className="text-[11px] text-muted-foreground leading-relaxed">
                          Cada enlace que crees genera una conexión visual en la **Vista de Grafo**. Así podrás descubrir relaciones entre tus ideas de forma orgánica.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === "shortcuts" && (
                <div className="space-y-4">
                  <div>
                    <h3 className="text-xs font-bold text-foreground mb-1">Atajos del Sistema</h3>
                    <p className="text-[11px] text-muted-foreground leading-relaxed">
                      Ahorra tiempo utilizando accesos rápidos de teclado desde cualquier parte del workspace:
                    </p>
                  </div>

                  <div className="divide-y divide-border/10">
                    {SHORTCUTS.map((s, idx) => (
                      <div key={idx} className="flex items-center justify-between py-2">
                        <div className="min-w-0">
                          <span className="text-xs font-bold text-foreground block">{s.label}</span>
                          <span className="text-[10px] text-muted-foreground block">{s.desc}</span>
                        </div>
                        <div className="flex gap-1 shrink-0">
                          {s.keys.map((k, j) => (
                            <kbd key={j} className="h-6 min-w-[20px] px-1.5 bg-secondary/80 border border-border/40 rounded shadow-xs text-[10px] font-mono font-bold flex items-center justify-center text-foreground">
                              {k}
                            </kbd>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {activeTab === "onboarding" && (
                <div className="space-y-4">
                  <div>
                    <h3 className="text-xs font-bold text-foreground mb-1">Guías e Introducción</h3>
                    <p className="text-[11px] text-muted-foreground leading-relaxed">
                      Si eres nuevo o quieres repasar el funcionamiento general del espacio de trabajo, puedes iniciar el recorrido guiado en cualquier momento.
                    </p>
                  </div>

                  <div className="p-4 bg-secondary/20 border border-border/30 rounded-xl space-y-3">
                    <div className="space-y-1">
                      <span className="text-xs font-bold text-foreground block">Tour Interactivo</span>
                      <p className="text-[11px] text-muted-foreground leading-relaxed">
                        Recorre paso a paso el explorador de notas, la paleta de comandos y el grafo de conocimiento.
                      </p>
                    </div>

                    <button
                      onClick={startTourNow}
                      className="w-full py-2 bg-primary text-primary-foreground hover:bg-primary/90 rounded-lg text-xs font-semibold flex items-center justify-center gap-1.5 transition-colors active:scale-[0.98]"
                    >
                      <Sparkles className="w-3.5 h-3.5" />
                      Iniciar tour interactivo ahora
                    </button>

                    <button
                      onClick={restartOnboarding}
                      className="w-full py-2 bg-secondary/60 border border-border/40 text-foreground/80 hover:text-foreground hover:bg-secondary rounded-lg text-xs font-medium flex items-center justify-center gap-1.5 transition-colors active:scale-[0.98]"
                    >
                      <RotateCcw className="w-3.5 h-3.5" />
                      Restablecer bienvenida completa
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-border/20 text-center text-[10px] text-muted-foreground bg-secondary/5">
              Wiki Arsenal Pro • Desarrollado con ❤️
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
